<template>
  <div class="ur-sim">
    <!-- Error bar -->
    <div v-if="errorMsg" class="ur-lo-error">
      <span>{{ errorMsg }}</span>
      <button class="ur-lo-error-close" @click="errorMsg = ''">&#10007;</button>
    </div>

    <!-- Left panel: input -->
    <div class="ur-sim-left">
      <!-- Landowner section -->
      <div>
        <div class="ur-sim-section-title">{{ $t('URBANRENEWAL_ADMIN.SIM.OWNER_SECTION') }}</div>
        <div
          v-for="e in ownerEntries"
          :key="e.key"
          class="ur-sim-row"
        >
          <span class="ur-sim-label">{{ $t('URBANRENEWAL_ADMIN.SIM.OWNER_NAME') }}</span>
          <UrbanRenewalSearchSelect
            :value="e.id"
            :options="ownerOptions"
            :placeholder="$t('URBANRENEWAL_ADMIN.SIM.PH_OWNER')"
            @input="e.id = $event"
          />
          <button
            v-if="ownerEntries.length > 1"
            class="ur-sim-remove-btn"
            @click="removeOwner(e.key)"
          >&#215;</button>
        </div>
        <button class="ur-sim-add-btn" @click="addOwner">
          + {{ $t('URBANRENEWAL_ADMIN.SIM.ADD_OWNER') }}
        </button>
      </div>

      <!-- House / Parking section -->
      <div>
        <div class="ur-sim-section-title">{{ $t('URBANRENEWAL_ADMIN.SIM.SELECT_SECTION') }}</div>

        <!-- House entries -->
        <div
          v-for="e in houseEntries"
          :key="e.key"
          class="ur-sim-row"
        >
          <span class="ur-sim-label">{{ $t('URBANRENEWAL_ADMIN.SIM.HOUSE_CODE') }}</span>
          <UrbanRenewalSearchSelect
            :value="e.unit_code"
            :options="houseOptions"
            :placeholder="$t('URBANRENEWAL_ADMIN.SIM.PH_HOUSE')"
            @input="e.unit_code = $event"
          />
          <button class="ur-sim-remove-btn" @click="removeHouse(e.key)">&#215;</button>
        </div>

        <!-- Parking entries -->
        <div
          v-for="e in parkEntries"
          :key="e.key"
          class="ur-sim-row"
        >
          <span class="ur-sim-label">{{ $t('URBANRENEWAL_ADMIN.SIM.PARKING_CODE') }}</span>
          <UrbanRenewalSearchSelect
            :value="e.unit_code"
            :options="parkOptions"
            :placeholder="$t('URBANRENEWAL_ADMIN.SIM.PH_PARKING')"
            @input="e.unit_code = $event"
          />
          <button class="ur-sim-remove-btn" @click="removePark(e.key)">&#215;</button>
        </div>

        <div class="ur-sim-btn-row">
          <button
            class="ur-sim-add-btn"
            :disabled="!houseAddable"
            @click="addHouse"
          >+ {{ $t('URBANRENEWAL_ADMIN.SIM.ADD_HOUSE') }}</button>
          <button
            class="ur-sim-add-btn"
            :disabled="!parkAddable"
            @click="addPark"
          >+ {{ $t('URBANRENEWAL_ADMIN.SIM.ADD_PARKING') }}</button>
        </div>
      </div>

      <!-- Calculate button -->
      <div class="ur-sim-btn-row">
        <button
          class="ur-sim-calc-btn"
          :disabled="!canCalc"
          @click="calc"
        ><span>{{ $t('URBANRENEWAL_ADMIN.SIM.CALC') }}</span><span>&#8594;</span></button>
      </div>
    </div>

    <!-- Right panel: result -->
    <div v-if="calculated && result" class="ur-sim-right">
      <!-- 基本資料：標題 + 全部清除 -->
      <div class="ur-sim-rp-header">
        <span class="ur-sim-rp-title">{{ $t('URBANRENEWAL_ADMIN.SIM.RESULT_BASIC') }}</span>
        <button class="ur-sim-clear-btn" @click="reset">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M19.3755 8.11714L18.4132 3L14.1688 8.1409L19.3755 8.11714Z" fill="currentColor"/>
            <path fill-rule="evenodd" clip-rule="evenodd" d="M6.526 7.45024C8.87016 4.611 13.0721 4.20965 15.9114 6.55381L17.1847 5.01154C13.4937 1.96413 8.03114 2.48588 4.98373 6.17689C1.93633 9.86791 2.45807 15.3305 6.14909 18.3779C9.8401 21.4253 15.3027 20.9035 18.3501 17.2125C19.8702 15.3714 20.5022 13.0874 20.2957 10.8856C20.2442 10.3358 19.7566 9.93182 19.2067 9.98338C18.6569 10.0349 18.2529 10.5225 18.3045 11.0724C18.4635 12.7688 17.9774 14.5226 16.8078 15.9392C14.4636 18.7784 10.2617 19.1798 7.42243 16.8356C4.58319 14.4915 4.18184 10.2895 6.526 7.45024Z" fill="currentColor"/>
          </svg>
          {{ $t('URBANRENEWAL_ADMIN.SIM.CLEAR_ALL') }}
        </button>
      </div>

      <div class="ur-sim-basic">
        <!-- 地主清單（勾選框可即時納入／排除試算）-->
        <div class="ur-sim-owner-col">
          <span class="ur-sim-owner-col-head">{{ $t('URBANRENEWAL_ADMIN.SIM.NAME_HEADER') }}</span>
          <div
            v-for="(o, idx) in result.owners"
            :key="'o-' + o.id"
            class="ur-sim-owner-item"
            :class="{ 'is-off': !o.checked }"
          >
            <span class="ur-sim-check" :class="{ 'is-on': o.checked }" @click="toggleOwner(o.id)">
              <svg v-if="o.checked" width="10" height="8" viewBox="0 0 10 8" fill="none">
                <path d="M1 4L3.5 6.5L9 1" stroke="#fff" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </span>
            <span class="ur-sim-owner-idx">{{ ownerItemLabel(idx) }}</span>
            <span class="ur-sim-owner-nm">{{ o.name }}</span>
          </div>
        </div>
        <!-- 值框 -->
        <div class="ur-sim-vbox-col">
          <div class="ur-sim-vbox">
            <span class="ur-sim-vbox-label">{{ $t('URBANRENEWAL_ADMIN.SIM.ALLOCATED') }}：</span>
            <span class="ur-sim-vbox-val">{{ fmt(result.totalAV) }}元</span>
          </div>
          <div class="ur-sim-vbox">
            <span class="ur-sim-vbox-label">{{ $t('URBANRENEWAL_ADMIN.SIM.SELECTABLE') }}：</span>
            <span class="ur-sim-vbox-val">{{ fmt(result.totalSV) }}元</span>
          </div>
        </div>
      </div>

      <hr class="ur-sim-divider" />

      <!-- 選配試算 -->
      <div class="ur-sim-rp-title2">{{ $t('URBANRENEWAL_ADMIN.SIM.RESULT_SELECTION') }}</div>
      <div class="ur-sim-select">
        <!-- 選配項目（可逐列刪除，即時重算）-->
        <div class="ur-sim-select-col">
          <div class="ur-sim-select-head">
            <span class="ur-sim-select-head-code">{{ $t('URBANRENEWAL_ADMIN.SIM.COL_UNIT_CODE') }}</span>
            <span class="ur-sim-select-head-dup">{{ $t('URBANRENEWAL_ADMIN.SIM.COL_DUP_GROUP') }}</span>
            <span class="ur-sim-select-head-sp"></span>
          </div>
          <div v-for="(h, idx) in result.houses" :key="'h-' + h.unit_code" class="ur-sim-select-row">
            <span class="ur-sim-select-idx">{{ itemLabel('HOUSE_ITEM', idx) }}</span>
            <span class="ur-sim-select-code">{{ h.unit_code }}</span>
            <span class="ur-sim-select-dup">{{ h.count }} {{ $t('URBANRENEWAL_ADMIN.SIM.GROUP_UNIT') }}</span>
            <button class="ur-sim-select-del" @click="removeSnapHouse(h.unit_code)">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
          <div v-for="(k, idx) in result.parks" :key="'p-' + k.unit_code" class="ur-sim-select-row">
            <span class="ur-sim-select-idx">{{ itemLabel('PARK_ITEM', idx) }}</span>
            <span class="ur-sim-select-code">{{ k.unit_code }}</span>
            <span class="ur-sim-select-dup">{{ k.count }} {{ $t('URBANRENEWAL_ADMIN.SIM.GROUP_UNIT') }}</span>
            <button class="ur-sim-select-del" @click="removeSnapPark(k.unit_code)">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m2 0v12a1 1 0 01-1 1H8a1 1 0 01-1-1V7h10z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                <path d="M10 11v5M14 11v5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
              </svg>
            </button>
          </div>
        </div>
        <!-- 值框 + 找補公式明細 + 差額 -->
        <div class="ur-sim-vbox-col">
          <div class="ur-sim-vbox">
            <span class="ur-sim-vbox-label">{{ $t('URBANRENEWAL_ADMIN.SIM.SELECTION_TOTAL') }}：</span>
            <span class="ur-sim-vbox-val">{{ fmt(result.actual) }}元</span>
          </div>

          <div class="ur-sim-vbox">
            <span class="ur-sim-vbox-label">{{ $t('URBANRENEWAL_ADMIN.SIM.SELECTION_RATIO') }}：</span>
            <input
              class="ur-sim-vbox-input"
              type="text"
              :value="fmtPct(result.ratio)"
              readonly
            />
          </div>

          <!-- 找補公式判斷 -->
          <div v-if="result.makeup !== null" class="ur-sim-formula">
            <template v-if="result.formulaCase === 1">
              {{ $t('URBANRENEWAL_ADMIN.SIM.FORMULA_CASE_A', { threshold: fmt(result.threshold) }) }}
            </template>
            <template v-else>
              <div class="ur-sim-formula-head">{{ $t('URBANRENEWAL_ADMIN.SIM.FORMULA_CASE_B', { threshold: fmt(result.threshold) }) }}</div>
              <div>(a) = {{ fmt(result.partA) }} 元</div>
              <div>(b) = {{ fmt(result.partB) }} 元</div>
              <div>(c) = {{ fmt(result.partC) }} 元</div>
            </template>
          </div>

          <!-- 差額（不足為紅色）-->
          <div class="ur-sim-vbox" :class="{ 'is-red': result.isDeficit }">
            <span class="ur-sim-vbox-label" :class="{ 'is-red': result.isDeficit }">{{ balanceLabel }}：</span>
            <span class="ur-sim-vbox-val" :class="{ 'is-red': result.isDeficit }">
              <template v-if="result.makeup === null">{{ $t('URBANRENEWAL_ADMIN.SIM.CANNOT_CALC') }}</template>
              <template v-else>{{ fmt(Math.abs(result.makeup)) }}元</template>
            </span>
          </div>
        </div>
      </div>
    </div>

    <!-- 未選建案 / 已選建案但尚未試算 的提示 -->
    <div v-else-if="!calculated" class="ur-sim-hint">
      {{ currentProjectId ? $t('URBANRENEWAL_ADMIN.SIM.HINT_CALC') : $t('URBANRENEWAL_ADMIN.SIM.SELECT_PROJECT') }}
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import UrbanRenewalDataService from '@/services/UrbanRenewalDataService';
import UrbanRenewalSearchSelect from '@/components/urbanrenewal/UrbanRenewalSearchSelect.vue';

export default {
  name: 'UrbanRenewalAdminSimulation',

  components: { UrbanRenewalSearchSelect },

  data() {
    return {
      sim: { landowners: [], housing_units: [], parking_units: [] },
      loading: false,
      errorMsg: '',
      keySeq: 0,
      ownerEntries: [{ key: 0, id: null }],
      houseEntries: [{ key: 0, unit_code: '' }],
      parkEntries: [{ key: 0, unit_code: '' }],
      calculated: false,
      snap: { owners: [], houses: [], parks: [] },
      // 右側基本資料：地主 id → 是否納入試算（勾選框，即時重算）
      checkedOwnerIds: {},
    };
  },

  computed: {
    ...mapState('urbanRenewalAdmin', ['currentProjectId']),

    ownerOptions() {
      return this.sim.landowners.map(function(l) {
        return { value: l.id, label: l.display_name };
      });
    },

    houseOptions() {
      var t = this.$t('URBANRENEWAL_ADMIN.SIM.GROUP_UNIT');
      return this.sim.housing_units.map(function(u) {
        return { value: u.unit_code, label: u.unit_code + '（' + u.current_selection_count + ' ' + t + '）' };
      });
    },

    parkOptions() {
      var t = this.$t('URBANRENEWAL_ADMIN.SIM.GROUP_UNIT');
      return this.sim.parking_units.map(function(u) {
        return { value: u.unit_code, label: u.unit_code + '（' + u.current_selection_count + ' ' + t + '）' };
      });
    },

    canCalc() {
      return this.ownerEntries.some(function(o) { return o.id; }) &&
        (this.houseEntries.some(function(h) { return h.unit_code; }) ||
         this.parkEntries.some(function(k) { return k.unit_code; }));
    },

    houseAddable() {
      return this.houseEntries.length < 4;
    },

    parkAddable() {
      return this.parkEntries.length < 4;
    },

    result() {
      if (!this.calculated) return null;

      var self = this;

      // Resolve selected landowners, houses, parkings from snap + sim data
      var owners = this.snap.owners
        .map(function(o) { return self.sim.landowners.find(function(l) { return l.id === o.id; }); })
        .filter(Boolean);
      var houses = this.snap.houses
        .map(function(h) { return self.sim.housing_units.find(function(u) { return u.unit_code === h.unit_code; }); })
        .filter(Boolean);
      var parks = this.snap.parks
        .map(function(k) { return self.sim.parking_units.find(function(u) { return u.unit_code === k.unit_code; }); })
        .filter(Boolean);

      // 僅計入「已勾選」地主（右側勾選框即時納入／排除）
      var checkedOwners = owners.filter(function(o) { return self.checkedOwnerIds[o.id]; });

      // Aggregate values（依已勾選地主）
      var AV = checkedOwners.reduce(function(s, o) { return s + (o.allocated_value || 0); }, 0);
      var SV = checkedOwners.reduce(function(s, o) { return s + (o.selectable_value || 0); }, 0);
      var PV = checkedOwners.reduce(function(s, o) { return s + (o.parking_value || 0); }, 0);
      var H = houses.reduce(function(s, h) { return s + (h.total_price || 0); }, 0);
      var P = parks.reduce(function(s, k) { return s + (k.value || 0); }, 0);
      var actual = H + P;
      var threshold = AV * 1.1;

      var makeup = null;
      var partA = null, partB = null, partC = null;

      if (actual <= threshold) {
        // Case A
        makeup = SV - actual;
      } else {
        // Case B: simple average of unit_price for houses that have it
        var ups = houses.map(function(h) { return h.unit_price; }).filter(function(v) { return v != null; });
        if (ups.length) {
          var up = ups.reduce(function(s, v) { return s + v; }, 0) / ups.length;
          if (up !== 0) {
            var sps = houses.map(function(h) { return h.sales_unit_price; }).filter(function(v) { return v != null; });
            var sp = sps.length ? sps.reduce(function(s, v) { return s + v; }, 0) / sps.length : 0;
            partA = ((AV - PV) * 1.1 - H) / up * sp * 0.95;
            partB = (SV - PV) - (AV - PV) * 1.1;
            partC = PV - P;
            makeup = partA + partB + partC;
          }
          // up === 0 → makeup stays null
        }
        // no houses with unit_price → makeup stays null
      }

      return {
        owners: owners.map(function(o) {
          return {
            id: o.id,
            name: o.display_name,
            checked: !!self.checkedOwnerIds[o.id],
            av: o.allocated_value || 0,
            sv: o.selectable_value || 0,
          };
        }),
        totalAV: AV,
        totalSV: SV,
        houses: houses.map(function(h) { return { unit_code: h.unit_code, count: h.current_selection_count }; }),
        parks: parks.map(function(k) { return { unit_code: k.unit_code, count: k.current_selection_count }; }),
        actual: actual,
        ratio: AV > 0 ? actual / AV : null,
        makeup: makeup,
        threshold: threshold,
        formulaCase: actual <= threshold ? 1 : 2,
        partA: partA,
        partB: partB,
        partC: partC,
        isDeficit: makeup !== null && makeup < 0,
      };
    },

    balanceLabel() {
      if (!this.result) return '';
      if (this.result.makeup === null) return this.$t('URBANRENEWAL_ADMIN.SIM.MAKEUP');
      return this.result.isDeficit
        ? this.$t('URBANRENEWAL_ADMIN.SIM.BALANCE_DEFICIT')
        : this.$t('URBANRENEWAL_ADMIN.SIM.REFUND');
    },
  },

  watch: {
    currentProjectId: {
      immediate: true,
      handler() {
        this.loadData();
      },
    },
  },

  methods: {
    async loadData() {
      if (!this.currentProjectId) {
        this.sim = { landowners: [], housing_units: [], parking_units: [] };
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      // Reset entries when project changes
      this.ownerEntries = [{ key: this.newKey(), id: null }];
      this.houseEntries = [{ key: this.newKey(), unit_code: '' }];
      this.parkEntries = [{ key: this.newKey(), unit_code: '' }];
      this.calculated = false;
      this.snap = { owners: [], houses: [], parks: [] };
      this.checkedOwnerIds = {};
      try {
        var res = await UrbanRenewalDataService.getSimulationData(this.currentProjectId);
        this.sim = res.data;
      } catch (err) {
        this.errorMsg = this.$t('URBANRENEWAL_ADMIN.SIM.LOAD_FAILED');
      } finally {
        this.loading = false;
      }
    },

    newKey() {
      return ++this.keySeq;
    },

    addOwner() {
      this.ownerEntries.push({ key: this.newKey(), id: null });
    },

    addHouse() {
      if (this.houseAddable) {
        this.houseEntries.push({ key: this.newKey(), unit_code: '' });
      }
    },

    addPark() {
      if (this.parkAddable) {
        this.parkEntries.push({ key: this.newKey(), unit_code: '' });
      }
    },

    removeOwner(key) {
      if (this.ownerEntries.length > 1) {
        this.ownerEntries = this.ownerEntries.filter(function(o) { return o.key !== key; });
      }
    },

    removeHouse(key) {
      this.houseEntries = this.houseEntries.filter(function(e) { return e.key !== key; });
    },

    removePark(key) {
      this.parkEntries = this.parkEntries.filter(function(e) { return e.key !== key; });
    },

    calc() {
      this.snap = {
        owners: this.ownerEntries.filter(function(o) { return o.id; }).map(function(o) { return { id: o.id }; }),
        houses: this.houseEntries.filter(function(h) { return h.unit_code; }).map(function(h) { return { unit_code: h.unit_code }; }),
        parks: this.parkEntries.filter(function(k) { return k.unit_code; }).map(function(k) { return { unit_code: k.unit_code }; }),
      };
      // 試算後預設全部地主納入
      var ids = {};
      this.snap.owners.forEach(function(o) { ids[o.id] = true; });
      this.checkedOwnerIds = ids;
      this.calculated = true;
    },

    toggleOwner(id) {
      this.$set(this.checkedOwnerIds, id, !this.checkedOwnerIds[id]);
    },

    ownerItemLabel(idx) {
      return this.$t('URBANRENEWAL_ADMIN.SIM.OWNER_ITEM') + String(idx + 1).padStart(2, '0') + '：';
    },

    itemLabel(prefixKey, idx) {
      return this.$t('URBANRENEWAL_ADMIN.SIM.' + prefixKey) + String(idx + 1).padStart(2, '0') + '：';
    },

    removeSnapHouse(code) {
      this.snap.houses = this.snap.houses.filter(function(h) { return h.unit_code !== code; });
    },

    removeSnapPark(code) {
      this.snap.parks = this.snap.parks.filter(function(k) { return k.unit_code !== code; });
    },

    reset() {
      this.ownerEntries = [{ key: this.newKey(), id: null }];
      this.houseEntries = [{ key: this.newKey(), unit_code: '' }];
      this.parkEntries = [{ key: this.newKey(), unit_code: '' }];
      this.calculated = false;
      this.snap = { owners: [], houses: [], parks: [] };
      this.checkedOwnerIds = {};
    },

    fmt(v) {
      // 設計稿：千分位、無小數（四捨五入）
      if (v == null) return '—';
      return Number(v).toLocaleString('zh-TW', { maximumFractionDigits: 0 });
    },

    fmtPct(v) {
      return v == null ? '—' : (v * 100).toFixed(2) + '%';
    },
  },
};
</script>
